import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation, faCircleCheck } from '@fortawesome/free-solid-svg-icons';

import { UserAuth } from '../../../authentication/AuthContext.jsx';
import AccessManagementAuthorization from './AccessManagementAuthorization.jsx';
import AccessManagementOrgRegistration from './org-management/AccessManagementOrgRegistration.jsx';
import AccessManagementInvitationAccept from './user-access-registration/AccessManagementInvitationAccept.jsx';
import AccessManagementApproval from './approval/AccessManagementApproval.jsx';
import AccessManagementAccessRequest from './access-request/AccessManagementAccessRequest.jsx';
import { SecondaryButton } from '../../../components/button/Button.jsx';
import { getInvitationById, acceptInvitation, createUserAndAcceptInvitation, declineInvitation, approveInvitation } from '../../../services/admin-settings/InviteManagementService.jsx';
import { getUserGameAccessRequests, processGameAccessRequest } from '../../../services/admin-settings/UserGameAccessService.jsx';
import { getUser, setPrimaryGoal, buildPreferencesPatch } from '../../../services/UserProfileService.jsx';
import { addOrg, setDashboardContext, setEffectivePermissions, setUserInformation, updateDashboardContext } from '../../../redux-store/ReduxStore.js';
import { resolveLandingPath } from '../../../utils/rbacUtils.js';
import { getEffectivePermissions } from '../../../services/admin-settings/UserPermissionService.jsx';
import { useSelector } from 'react-redux';

import './AccessManagement.css';

const AccessManagement = () => {
    const [searchParams] = useSearchParams();
    const { user, logOut } = UserAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userRole = useSelector(state => state.dashboardContext.userRole);
    const effectivePermissions = useSelector(state => state.effectivePermissions);

    // Invitation emails used to join their query string with the "&amp;" HTML entity rather than a
    // plain "&" (fixed in BaseEmailHandler.accountUrl). A URL like
    //   /account?invitationType=INVITATION&amp;invitationId=…&amp;invitationLevel=…
    // parses to parameters literally NAMED "amp;invitationId" and "amp;invitationLevel", so every
    // param after the first read as absent and a signed-in recipient was forwarded straight to their
    // landing page — the invitation never rendered. Links already sitting in inboxes still carry it,
    // so read the mangled spelling as a fallback. Safe to delete once those have expired.
    const param = (name) => searchParams.get(name) ?? searchParams.get(`amp;${name}`);

    const invitationType = param('invitationType')?.toUpperCase();
    const invitationLevel = param('invitationLevel')?.toUpperCase();
    const invitationId = param('invitationId');
    // Game-access review flow (owner's "Review request" email button) — see the branch below.
    const requestId = param('requestId');

    const navigateToLanding = async (roleOverride) => {
        const role = roleOverride || userRole;
        // Hydrate user details + permissions exactly the way Login does. Two reasons this must mirror
        // login rather than only refetch permissions:
        //   1) setUserInformation flips userDetailsLoaded — without it the DashboardLayout gate
        //      (effectivePermissions && userDetailsLoaded) never unblocks → the screen hangs on the
        //      loader (a full reload "fixed" it only because that re-ran login's own hydration).
        //   2) we land via the user's defaultLandingPage preference (resolveLandingPath), the same as
        //      login — landing straight at getFirstAccessiblePath resolved to NO_ACCESS at org scope
        //      for a sub-org-scoped member and fell through to the ungated Profile link.
        let landingCtx = { userRole: role };
        try {
            const userData = await getUser({ accessToken: user.accessToken });
            if (userData?.user) {
                const orgs = userData.user.orgUserStates
                    ? Object.entries(userData.user.orgUserStates).map(([orgId, orgState]) => ({ orgId, orgState }))
                    : [];
                dispatch(setUserInformation({
                    userId: userData.user.userId || null,
                    userName: userData.user.userName || null,
                    orgs,
                    roleId: userData.user.roleId || null,
                    superUser: userData.user.superUser || false,
                }));
                const prefs = buildPreferencesPatch(userData.user);
                dispatch(updateDashboardContext(prefs));
                landingCtx = { ...prefs, userRole: role };
            }
        } catch (e) {
            console.error("Error loading user details:", e);
        }

        // Permissions change on acceptance — always refetch.
        let perms = null;
        try {
            perms = await getEffectivePermissions({ accessToken: user.accessToken });
            dispatch(setEffectivePermissions(perms));
        } catch (e) {
            console.error("Error fetching permissions:", e);
        }

        navigate(resolveLandingPath(landingCtx));
    };
    const [invitation, setInvitation] = useState(null);
    // Keyed off invitationId: the fetch effect below bails out when there's no id, so a hardcoded
    // `true` left the "Loading invitation details..." gate stuck forever on every link that carries
    // no invitationId (the access-request review, and the param-less "Open GeoPlay" button).
    const [invitationLoading, setInvitationLoading] = useState(!!invitationId);
    const [userData, setUserData] = useState(null);
    const [userCheckDone, setUserCheckDone] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isDecliningApproval, setIsDecliningApproval] = useState(false);
    const [accessRequest, setAccessRequest] = useState(null);
    // The requestId a lookup has actually completed for — distinguishes "not fetched yet" from
    // "fetched, no match" without a second flag, and tags the result so a changed id can't paint
    // the previous request's details.
    const [checkedRequestId, setCheckedRequestId] = useState(null);
    const [isApprovingRequest, setIsApprovingRequest] = useState(false);
    const [isRejectingRequest, setIsRejectingRequest] = useState(false);

    useEffect(() => {
        const fetchInvitation = async () => {
            if (!invitationId || !user?.accessToken) return;
            setInvitationLoading(true);
            try {
                const invitationData = await getInvitationById({
                    accessToken: user?.accessToken,
                    invitationId
                });
                setInvitation(invitationData.invitation);
            } catch (err) {
                console.error('Failed to fetch invitation:', err);
            } finally {
                setInvitationLoading(false);
            }
        };

        fetchInvitation();
    }, [invitationId, user?.accessToken]);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!user?.accessToken) return;
            try {
                const result = await getUser({ accessToken: user.accessToken });
                setUserData(result?.user || null);
            } catch {
                setUserData(null);
            } finally {
                setUserCheckDone(true);
            }
        };

        fetchUserData();
    }, [user?.accessToken]);

    // Resolve the access request behind a "Review request" email link. There's no get-by-id
    // endpoint, and we don't need one: GET /user-game-access?approvingUserId is already scoped
    // server-side to the requests this user may approve, so finding the id in that list IS the
    // authorization check — a miss means this account can't review it (see the branch below).
    useEffect(() => {
        if (!requestId || !user?.accessToken || !userCheckDone) return;
        const approvingUserId = userData?.userId || user?.email;
        if (!approvingUserId) {
            setCheckedRequestId(requestId);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const resp = await getUserGameAccessRequests({
                    accessToken: user.accessToken,
                    approvingUserId,
                });
                if (cancelled) return;
                // Endpoint serializes the list under `accessRequests`
                // (GetUserGameAccessRequestsResponse @JsonProperty).
                setAccessRequest((resp?.accessRequests || []).find(r => r.requestId === requestId) || null);
            } catch (err) {
                console.error('Failed to fetch access request:', err);
                if (!cancelled) setAccessRequest(null);
            } finally {
                if (!cancelled) setCheckedRequestId(requestId);
            }
        })();
        return () => { cancelled = true; };
    }, [requestId, user?.accessToken, user?.email, userData?.userId, userCheckDone]);

    // The access-resolved email's "Open GeoPlay" button lands here with no params — there's nothing
    // to action, so hand a signed-in visitor straight to their landing page. Ref-guarded because
    // navigateToLanding is re-created every render and can't go in the dep list.
    const forwardedToLandingRef = useRef(false);
    useEffect(() => {
        if (!user?.accessToken || !userCheckDone) return;
        if (invitationId || requestId || forwardedToLandingRef.current) return;
        forwardedToLandingRef.current = true;
        navigateToLanding();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.accessToken, userCheckDone, invitationId, requestId]);

    const handleAccept = async (formData) => {
        setIsAccepting(true);
        try {
            if (formData?.name) {
                // Brand-new invitee from the guided flow: create their account
                // (persisting the entered display name) AND accept in one call.
                await createUserAndAcceptInvitation({
                    accessToken: user?.accessToken,
                    invitationId,
                    name: formData.name,
                    selectedPreferenceIds: [],
                });
            } else {
                // Existing user: just accept (they already have a name on record).
                await acceptInvitation({ accessToken: user?.accessToken, invitationId });
            }

            // New users also pick a primary goal in the guided flow — persist it
            // (best-effort; a goal failure shouldn't block joining).
            if (formData?.goal) {
                try {
                    await setPrimaryGoal({ accessToken: user?.accessToken, goal: formData.goal });
                } catch (e) {
                    console.error('Failed to set primary goal:', e);
                }
            }

            // Establish org context + derived role and land on the first
            // accessible page. Shared with the already-accepted ("Go to
            // Dashboard") path so both stay in sync.
            await enterDashboard();
        } catch (err) {
            console.error('Failed to accept invitation:', err);
        } finally {
            setIsAccepting(false);
        }
    };

    // Returning user reopening an already-accepted / approved link. The
    // acceptance already happened in a past session, so we don't re-accept —
    // but we must still establish the org context + derived role exactly as a
    // fresh acceptance would, otherwise the dashboard loads with no selected
    // org/role and shows the empty "?" context, the wrong left-nav, and a
    // "no permission" chat box.
    const enterDashboard = async () => {
        // Co-owner == owner for authorization, so map co-owner tiers to their
        // owner role for the dashboard context (drives landing + nav until the
        // freshly-fetched permissions take over).
        const derivedRole =
            (invitationLevel === 'ORG_OWNER' || invitationLevel === 'ORG_CO_OWNER') ? 'ORG_OWNER'
            : (invitationLevel === 'GAME_OWNER' || invitationLevel === 'GAME_CO_OWNER') ? 'GAME_OWNER'
            : 'REGULAR_USER';

        const acceptedOrgId = invitation?.orgDetails?.orgId;
        if (acceptedOrgId) {
            dispatch(addOrg({ orgId: acceptedOrgId, orgState: 'ACTIVE' }));
            dispatch(setDashboardContext({
                selectedOrgId: acceptedOrgId,
                selectedGameId: null,
                selectedGameName: null,
                selectedGameClientId: null,
                selectedRegionIds: [],
                userRole: derivedRole
            }));
        }
        sessionStorage.removeItem('permissionsContextKey');
        await navigateToLanding(derivedRole);
    };

    const handleDecline = async () => {
        setIsDeclining(true);
        try {
            await declineInvitation({
                accessToken: user?.accessToken,
                invitationId
            });
            await logOut();
            navigate('/');
        } catch (err) {
            console.error('Failed to decline invitation:', err);
        } finally {
            setIsDeclining(false);
        }
    };

    const handleApprove = async () => {
        setIsApproving(true);
        try {
            await approveInvitation({
                accessToken: user?.accessToken,
                invitationId
            });
            await navigateToLanding();
        } catch (err) {
            console.error('Failed to approve invitation:', err);
        } finally {
            setIsApproving(false);
        }
    };

    const handleDeclineApproval = async () => {
        setIsDecliningApproval(true);
        try {
            await declineInvitation({
                accessToken: user?.accessToken,
                invitationId
            });
            await navigateToLanding();
        } catch (err) {
            console.error('Failed to decline invitation request:', err);
        } finally {
            setIsDecliningApproval(false);
        }
    };

    // Approve / reject a game-access request from the email review card. The server does the
    // promotion into the game and notifies the requester, exactly as it does from the Approval
    // Queue — then we drop the owner on their landing page.
    const processAccessRequest = async (status, setBusy) => {
        setBusy(true);
        try {
            await processGameAccessRequest({
                accessToken: user?.accessToken,
                requestId,
                status
            });
            await navigateToLanding();
        } catch (err) {
            console.error(`Failed to ${status.toLowerCase()} access request:`, err);
        } finally {
            setBusy(false);
        }
    };

    const handleLogout = async () => {
        await logOut();
        navigate('/');
    };

    const isEmailMatch = () => {
        if (!invitation || !user) return false;
        const invitedEmail = invitation.targetUserId?.toLowerCase();
        const loggedInEmail = user.email?.toLowerCase();
        return invitedEmail === loggedInEmail;
    };

    const invitationTypeConfig = {
        // The org-registration UI is for the FIRST org owner setting up a new org
        // (org type/region/currency/domains). Everyone else — co-owners and game
        // owner/co-owner/users — is JOINING an existing org/game, so they get the
        // lightweight guided accept (org name + role + name + goal), which also
        // avoids the org-registration form's empty fields + geo-data fetches.
        ORG_OWNER: {
            invitationComponent: AccessManagementOrgRegistration,
        },
        ORG_CO_OWNER: {
            invitationComponent: AccessManagementInvitationAccept,
        },
        GAME_OWNER: {
            invitationComponent: AccessManagementInvitationAccept,
        },
        GAME_CO_OWNER: {
            invitationComponent: AccessManagementInvitationAccept,
        },
        USER: {
            invitationComponent: AccessManagementInvitationAccept,
        }
    };

    // Not authenticated — show login/signup
    if (!user?.accessToken) {
        return (
            <div className="access-management-container">
                <AccessManagementAuthorization />
            </div>
        );
    }

    // ── Game-access request review (owner's "Review request" email button) ────
    if (requestId) {
        if (!userCheckDone || checkedRequestId !== requestId) {
            return (
                <div className="access-management-container">
                    <p>Loading access request details...</p>
                </div>
            );
        }

        // Not in this user's approvable list — they're signed in as someone else, or it's gone.
        // Same shape as the invitation Email Mismatch card.
        if (!accessRequest) {
            return (
                <div className="access-management-container">
                    <div className="access-management-mismatch-card">
                        <FontAwesomeIcon icon={faCircleExclamation} className="access-management-mismatch-icon" />
                        <h2 className="access-management-mismatch-title">Request Not Available</h2>
                        <p className="access-management-mismatch-text">
                            This access request isn't available for you to review. It may have been withdrawn,
                            or you may be signed in with a different account than the one it was sent to.
                        </p>
                        <SecondaryButton onClick={handleLogout}>
                            Log Out
                        </SecondaryButton>
                    </div>
                </div>
            );
        }

        // Already actioned (GameAccessStatus is PENDING / APPROVED / REJECTED) — show the outcome
        // instead of re-offering the decision, mirroring the resolved-invitation cards below.
        if (accessRequest.accessStatus && accessRequest.accessStatus !== 'PENDING') {
            const approved = accessRequest.accessStatus === 'APPROVED';
            return (
                <div className="access-management-container">
                    <div className="access-management-mismatch-card">
                        <FontAwesomeIcon
                            icon={approved ? faCircleCheck : faCircleExclamation}
                            className="access-management-mismatch-icon"
                        />
                        <h2 className="access-management-mismatch-title">
                            {approved ? 'Request Already Approved' : 'Request Already Rejected'}
                        </h2>
                        <p className="access-management-mismatch-text">
                            {`This access request has already been ${approved ? 'approved' : 'rejected'}`}
                            {accessRequest.processedBy ? ` by ${accessRequest.processedBy}` : ''}.
                        </p>
                        <SecondaryButton onClick={() => navigateToLanding()}>
                            Go to Home
                        </SecondaryButton>
                    </div>
                </div>
            );
        }

        return (
            <div className="access-management-container">
                <AccessManagementAccessRequest
                    request={accessRequest}
                    onApprove={() => processAccessRequest('APPROVED', setIsApprovingRequest)}
                    onReject={() => processAccessRequest('REJECTED', setIsRejectingRequest)}
                    approveLoading={isApprovingRequest}
                    rejectLoading={isRejectingRequest}
                />
            </div>
        );
    }

    // Authenticated but invitation or user check still loading
    if (invitationLoading || !userCheckDone) {
        return (
            <div className="access-management-container">
                <p>Loading invitation details...</p>
            </div>
        );
    }

    // Authenticated and invitation loaded — check email match
    if (invitation && !isEmailMatch()) {
        return (
            <div className="access-management-container">
                <div className="access-management-mismatch-card">
                    <FontAwesomeIcon icon={faCircleExclamation} className="access-management-mismatch-icon" />
                    <h2 className="access-management-mismatch-title">Email Mismatch</h2>
                    <p className="access-management-mismatch-text">
                        Your logged-in email does not match the invited user's email. Please log out and sign in with the correct account.
                    </p>
                    <SecondaryButton onClick={handleLogout}>
                        Log Out
                    </SecondaryButton>
                </div>
            </div>
        );
    }

    // GAME_OWNER_INVITE: show approval UI for org owner
    const isApprovalFlow = invitation?.invitationType === 'GAME_OWNER_INVITE' || invitationType === 'GAME_OWNER_INVITE';

    // Guard already-resolved links. An invitation that's been accepted /
    // declined / revoked / expired (or, for the approval flow, already
    // approved) is no longer actionable — reopening it previously re-rendered
    // the accept / approve UI. Show a clear status message instead.
    // NOTE: APPROVED is intentionally NOT terminal for the accept flow — it
    // means the org approved a multi-step invite and it's now the invitee's
    // turn to accept, so that path must still reach the accept component.
    const status = invitation?.invitationStatus;
    const terminalStatuses = isApprovalFlow
        ? ['APPROVED', 'ACCEPTED', 'DECLINED', 'REVOKED', 'EXPIRED']
        : ['ACCEPTED', 'DECLINED', 'REVOKED', 'EXPIRED'];

    if (invitation && terminalStatuses.includes(status)) {
        const RESOLVED = {
            ACCEPTED: { icon: faCircleCheck,       title: 'Invitation Already Accepted', text: "You've already accepted this invitation — there's nothing more to do here.",                primary: { label: 'Go to Home', onClick: enterDashboard } },
            APPROVED: { icon: faCircleCheck,       title: 'Request Already Approved',     text: "You've already approved this access request.",                                              primary: { label: 'Go to Home', onClick: enterDashboard } },
            DECLINED: { icon: faCircleExclamation, title: 'Invitation Declined',          text: 'This invitation was declined and is no longer valid.',                                       primary: { label: 'Log Out',         onClick: handleLogout } },
            REVOKED:  { icon: faCircleExclamation, title: 'Invitation Revoked',           text: 'This invitation has been revoked by the sender and can no longer be used.',                  primary: { label: 'Log Out',         onClick: handleLogout } },
            EXPIRED:  { icon: faCircleExclamation, title: 'Invitation Expired',           text: 'This invitation link has expired. Please ask the sender for a new invite.',                  primary: { label: 'Log Out',         onClick: handleLogout } },
        };
        const cfg = RESOLVED[status] || RESOLVED.EXPIRED;
        return (
            <div className="access-management-container">
                <div className="access-management-mismatch-card">
                    <FontAwesomeIcon icon={cfg.icon} className="access-management-mismatch-icon" />
                    <h2 className="access-management-mismatch-title">{cfg.title}</h2>
                    <p className="access-management-mismatch-text">{cfg.text}</p>
                    <SecondaryButton onClick={cfg.primary.onClick}>
                        {cfg.primary.label}
                    </SecondaryButton>
                </div>
            </div>
        );
    }

    if (isApprovalFlow) {
        return (
            <div className="access-management-container">
                <AccessManagementApproval
                    invitation={invitation}
                    onApprove={handleApprove}
                    onDecline={handleDeclineApproval}
                    approveLoading={isApproving}
                    declineLoading={isDecliningApproval}
                />
            </div>
        );
    }

    // Email matches — show invitation component. Fall back to the guided accept
    // component for any unmapped level so an invite never renders a blank page.
    const InvitationComponent = invitationTypeConfig[invitationLevel]?.invitationComponent
        || AccessManagementInvitationAccept;

    return (
        <div className="access-management-container">
            {InvitationComponent && (
                <InvitationComponent
                    invitation={invitation}
                    onAccept={handleAccept}
                    onDecline={handleDecline}
                    existingUser={userData}
                    acceptLoading={isAccepting}
                    declineLoading={isDeclining}
                />
            )}
        </div>
    );
};

export default AccessManagement;
